// Typed client for the dispatcher API. Every call carries the Supabase access
// token so the server can enforce per-user row-level security.

import { supabase } from "./supabase";

export type PlatformId = "youtube" | "tiktok" | "instagram" | "x" | "facebook";
export type MediaKind = "video" | "image" | "text";
export type Visibility = "public" | "unlisted" | "private";

export interface PlatformInfo {
  id: PlatformId;
  name: string;
  glyph: string;
  accent: string;
  supports: MediaKind[];
  live: boolean;
  configured: boolean;
  docsPath: string;
}

export interface ConnectedAccount {
  platform: PlatformId;
  accountId: string;
  displayName: string;
  /** Public @handle/username when the platform exposes one (e.g. TikTok), used
   * to link the account chip to its public page. Absent when unknown. */
  username?: string;
  connectedAt: number;
  scope?: string;
  /** Set by the server when the stored token can no longer publish (revoked,
   * expired, or scope-changed). The account is still "connected" but must be
   * re-authorised before it will publish — surfaced as an amber "Reconnect
   * needed" state and excluded from `will_publish` (see lib/platformStatus). */
  needsReconnect?: boolean;
}

export interface DispatchResult {
  platform: PlatformId;
  ok: boolean;
  url?: string;
  postId?: string;
  error?: string;
}

/** Normalized public engagement stats for one published post leg. Every field
 * is optional: platforms expose different metrics, and some (e.g. X impressions
 * on the free tier) may be absent even for a supported platform. A field is
 * present only when a real number came back — never a zero placeholder. */
export interface PostMetrics {
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
}

export interface DispatchRecord {
  id: string;
  at: number;
  title: string;
  results: DispatchResult[];
  /** Per-platform public stats, filled in asynchronously by the metrics-refresh
   * cron. A platform key is present only once its stats have been fetched; each
   * carries the ISO time of that fetch. Absent for platforms without a stats
   * endpoint under our scopes (e.g. TikTok). */
  metrics?: Partial<Record<PlatformId, PostMetrics & { fetchedAt: string }>>;
  /** The targeted platforms + the full draft, persisted so a failed post can be
   * re-posted in one click. Absent on posts sent before this was captured. */
  platforms?: PlatformId[];
  draft?: PostDraft;
}

export type TikTokPrivacyLevel =
  | "PUBLIC_TO_EVERYONE"
  | "MUTUAL_FOLLOW_FRIENDS"
  | "FOLLOWER_OF_CREATOR"
  | "SELF_ONLY";

/** TikTok Direct-Post options chosen by the creator in the composer. */
export interface TikTokOptions {
  privacyLevel: TikTokPrivacyLevel;
  disableComment: boolean;
  disableDuet: boolean;
  disableStitch: boolean;
  commercialContent: boolean;
  yourBrand: boolean;
  brandedContent: boolean;
}

/** YouTube upload options the creator must supply before an upload (ToS §9.1). */
export interface YouTubeOptions {
  /** The creator's own "Made for Kids" designation (ToS §9.1(iv)). There is no
   * default: Broadside never makes this declaration on the user's behalf. */
  madeForKids: boolean;
}

/** Creator account info from TikTok's creator_info query. */
export interface TikTokCreatorInfo {
  creatorAvatarUrl: string;
  creatorNickname: string;
  creatorUsername: string;
  privacyLevelOptions: string[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxVideoPostDurationSec: number;
}

/** Per-platform text override (AI-tailored caption). Provided fields replace the
 * base draft for that platform only. */
export interface PerPlatformOverride {
  title?: string;
  body?: string;
  tags?: string[];
}

export interface PostDraft {
  title: string;
  body: string;
  tags: string[];
  visibility: Visibility;
  media: { kind: MediaKind; url?: string; mimeType?: string } | null;
  tiktok?: TikTokOptions;
  /** YouTube upload options; present only when YouTube is a target. */
  youtube?: YouTubeOptions;
  /** Per-platform AI-tailored overrides; base draft is the fallback. */
  perPlatform?: Partial<Record<PlatformId, PerPlatformOverride>>;
}

/** One AI-tailored caption suggestion returned by /api/ai/captions. */
export interface PlatformCaption {
  title: string;
  body: string;
  tags: string[];
}

/** Response from the AI caption-tailoring endpoint. `configured:false` means
 * ANTHROPIC_API_KEY is not set on the server. */
export interface AiCaptionsResponse {
  configured: boolean;
  suggestions?: Partial<Record<PlatformId, PlatformCaption>>;
}

export interface ScheduledPost {
  id: string;
  at: number;
  createdAt: number;
  platforms: PlatformId[];
  draft: PostDraft;
  /** Client-supplied dedupe key, echoed back when idempotent scheduling was used. */
  idempotencyKey?: string;
}

/** A composed-but-not-yet-sent post, saved to finish later. Workspace-scoped.
 * `draft` is the same PostDraft a dispatch/schedule is built from, so a saved
 * draft reopens into the composer exactly as it was left. */
export interface Draft {
  id: string;
  /** Optional label; falls back to the draft's own title in the UI. */
  title: string | null;
  platforms: PlatformId[];
  draft: PostDraft;
  updatedAt: number;
  createdAt: number;
}

/** Kind of reusable snippet: a caption body or a group of hashtags/tags. */
export type SnippetKind = "caption" | "hashtags";

/** A reusable caption or hashtag group the creator inserts while composing.
 * `content` is raw text: a caption body, or a comma-separated tag string. */
export interface Snippet {
  id: string;
  kind: SnippetKind;
  name: string;
  content: string;
  updatedAt: number;
  createdAt: number;
}

/** A workspace (brand): its own set of connected accounts, scheduled posts, and history. */
export interface Workspace {
  id: string;
  name: string;
  createdAt: number;
}

/** One Facebook Page the operator manages, as shown in the Page picker. The
 * per-Page access token is never sent to the browser — only id, name, and
 * whether it can be published to. */
export interface FacebookPage {
  id: string;
  name: string;
  canPublish: boolean;
}

/** Response from GET /api/facebook/pages: every Page plus the currently selected
 * one (null until the operator picks). */
export interface FacebookPagesResponse {
  pages: FacebookPage[];
  selectedPageId: string | null;
}

export interface AccountsResponse {
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  history: DispatchRecord[];
  scheduled: ScheduledPost[];
  /** Whether direct file upload (Cloudflare R2) is configured on the server. */
  uploadConfigured: boolean;
  /** Whether AI per-platform caption tailoring is configured on the server. */
  aiConfigured: boolean;
  /** Whether the signed-in user is the deployment owner (in the owner
   * allowlist). Gates whether the owner-only Ops link is shown — the actual
   * data endpoint 403s non-owners regardless. Absent on older servers → falsy. */
  isOwner?: boolean;
  /** The same user's connections in their OTHER workspaces (accountId lets the
   * Connections page flag the exact same social account connected elsewhere, so
   * they don't unknowingly double-connect and double-post). Absent on older
   * servers → treated as none. */
  otherWorkspaceConnections?: {
    platform: PlatformId;
    accountId: string;
    displayName: string;
    workspaceId: string;
  }[];
}

/** Founder Ops Dashboard snapshot — owner-only, system-wide across all users.
 * Every aggregate is independently nullable: the server returns the rest with a
 * null field rather than failing the whole page if one aggregate errors. */
/** One row of the Command Center Users table (owner-only). Mirrors the CSV
 * export's shape. Dates are ISO strings; nulls where a user has no billing yet. */
export interface AdminUserRow {
  email: string;
  tier: string;
  subscription_status: string;
  paid: boolean | null;
  marketing_opt_in: boolean | null;
  marketing_opt_in_at: string | null;
  credits: number | null;
  joined: string | null;
  last_active: string | null;
  posts: number | null;
}

export interface AdminOverview {
  /** Distinct users (proxied by distinct workspace owners). */
  users: number | null;
  connectedAccounts: { total: number; byPlatform: Record<string, number> } | null;
  posts: {
    total: number;
    last30d: number;
    /** Dispatches with ≥1 failed leg. */
    failed: number;
    /** Dispatches whose every leg succeeded. */
    succeeded: number;
    /** failed / total, in [0,1]; 0 when there are no dispatches. */
    failureRate: number;
  } | null;
  scheduledPending: number | null;
  revenue: {
    /** Monthly recurring revenue in whole USD, across all users. */
    mrr: number;
    activeByPlan: { starter: number; pro: number; agency: number };
    /** Sum of every account's credit balance. */
    creditsOutstanding: number;
    /** Monthly price per plan (USD). */
    prices: { starter: number; pro: number; agency: number };
    /** Active paying subscribers (active + trialing). */
    paying: number;
    /** How many of those are in a trial. */
    trialing: number;
    /** Billing accounts with no active paid plan. */
    freeAccounts: number;
    /** All billing accounts. */
    totalAccounts: number;
  } | null;
  recentFailures:
    | Array<{
        id: string;
        at: number;
        title: string;
        failures: Array<{ platform: string; reason: string }>;
      }>
    | null;
  /** Early-access waitlist: exact total + the newest signups (full list is the
   * CSV export). Null when the aggregate failed. */
  waitlist: { total: number; recent: Array<{ email: string; at: number }> } | null;
  /** 30-day daily growth: signups, posts, and waitlist joins per day. */
  growth: Array<{ day: string; signups: number; posts: number; waitlist: number }> | null;
  /** Top users by lifetime posts, with plan/credits/platform/last-active. */
  topUsers:
    | Array<{
        userId: string;
        email: string;
        plan: string;
        status: string | null;
        credits: number;
        posts: number;
        platforms: number;
        lastActive: number | null;
        joined: number;
        /** Successful X posts (the one per-post platform cost, ~$0.20 each). */
        xPosts: number;
        /** Successful posts per platform, for the drill-down. */
        byPlatform: Record<string, number>;
      }>
    | null;
  /** Most recent dispatches across all users, for the live activity feed. */
  activity:
    | Array<{
        id: string;
        at: number;
        email: string;
        title: string;
        platforms: string[];
        okCount: number;
        failCount: number;
      }>
    | null;
  /** System health: reconnect backlog + scheduler pending/overdue. */
  health: {
    reconnectNeeded: number;
    accountsWithTrouble: number;
    pendingScheduled: number;
    overdueScheduled: number;
    oldestOverdue: number | null;
  } | null;
  /** Daily MRR history (from the snapshot job) for the revenue trend chart. */
  mrrHistory: Array<{ day: string; mrr: number; paying: number }> | null;
}

/** Response from the presign endpoint. `configured:false` means R2 is not set up. */
export interface PresignResponse {
  configured: boolean;
  uploadUrl?: string;
  publicUrl?: string;
  key?: string;
  requiredHeaders?: Record<string, string>;
}

export type NotificationSeverity = "info" | "warning" | "error";

/** One in-app notification shown in the topbar notifications center. Minted
 * server-side on events (a scheduled post failing, an account needing
 * reconnection); the client only reads, marks-read, and dismisses. `ref` carries
 * deep-link context (e.g. `{ dispatchId, platforms }` or `{ platform }`). */
export interface Notification {
  id: string;
  type: string;
  severity: NotificationSeverity;
  title: string;
  body?: string;
  ref?: Record<string, unknown>;
  /** Epoch ms when marked read; absent while unread. */
  readAt?: number;
  createdAt: number;
}

/** Response from GET /api/notifications: the newest notifications plus the
 * live unread count for the topbar badge. */
export interface NotificationsResponse {
  notifications: Notification[];
  unread: number;
}

/** A notification type whose delivery the user can tune in the Control Center. */
export type NotifPrefType = "dispatch_failed" | "reconnect_needed" | "credits_low";

/** Per-channel delivery preference for one notification type. */
export interface NotifChannelPref {
  inApp: boolean;
  email: boolean;
}

/** All of a user's per-type notification preferences, keyed by notification type.
 * An absent key (or channel) means the default: on. */
export type NotifPrefs = Record<string, NotifChannelPref>;

export type BillingPlan = "starter" | "pro" | "agency";

/** Price + credit grant for one purchasable item, as reported by the server so
 * the UI never hardcodes dollar amounts or credit counts. `priceLabel` is a
 * ready-to-render string ("$9"); `credits` is the number of credits it grants. */
export interface PricingItem {
  /** Ready-to-render price, e.g. "$9". */
  priceLabel: string;
  /** Credits this item grants (publishes spend a platform-weighted amount). */
  credits: number;
}

/** Server-supplied pricing for the plans and the one-time top-up, so Billing
 * renders labels/amounts from the source of truth instead of "$9/$24/$59/$6". */
export interface BillingPricing {
  starter: PricingItem;
  pro: PricingItem;
  agency: PricingItem;
  topup: PricingItem;
}

export interface BillingState {
  configured: boolean;
  plan: BillingPlan | null;
  status: string | null;
  creditsBalance: number;
  autoTopup: boolean;
  currentPeriodEnd: string | null;
  hasPaymentMethod: boolean;
  /** Present when the server reports its pricing; Billing falls back to static
   * copy when it is absent (older server). */
  pricing?: BillingPricing;
}

// The active workspace (brand) the client is operating in. Sent on every request
// as `x-workspace-id`; the server validates it and falls back to the user's
// default if it is missing or not theirs. Persisted so a reload keeps the brand.
const WORKSPACE_KEY = "broadside.workspace";
let activeWorkspaceId: string | null = localStorage.getItem(WORKSPACE_KEY);

/** Set (or clear) the active workspace, persisting it across reloads. */
export function setActiveWorkspaceId(id: string | null): void {
  activeWorkspaceId = id;
  if (id) localStorage.setItem(WORKSPACE_KEY, id);
  else localStorage.removeItem(WORKSPACE_KEY);
}

/** The active workspace id, or null if none has been chosen yet. */
export function getActiveWorkspaceId(): string | null {
  return activeWorkspaceId;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const res = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(activeWorkspaceId ? { "x-workspace-id": activeWorkspaceId } : {}),
      ...(init?.headers || {}),
    },
  });
  const text = await res.text();
  // A non-JSON body (an HTML error page, a proxy timeout, an empty 502) must not
  // surface a raw SyntaxError to the user. Parse defensively and fall back to a
  // status-based message so a failed request always reads like a sentence.
  let body: Record<string, unknown> = {};
  if (text) {
    try {
      body = JSON.parse(text) as Record<string, unknown>;
    } catch {
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      throw new Error("Received an unexpected response from the server.");
    }
  }
  if (!res.ok) {
    const message = typeof body.error === "string" ? body.error : `Request failed (${res.status})`;
    throw new Error(message);
  }
  return body as T;
}

export const api = {
  accounts: () => req<AccountsResponse>("/api/accounts"),
  /** Owner-only system-wide ops snapshot. The server 403s non-owners. */
  adminOverview: () => req<AdminOverview>("/api/admin/overview"),
  /** Owner-only: the full user list for the Command Center Users table. */
  adminUsers: () => req<{ users: AdminUserRow[] }>("/api/admin/users"),
  /** Owner-only: the full early-access waitlist as a downloadable CSV Blob. The
   * server 403s non-owners. Not JSON, so it bypasses the `req` helper. */
  waitlistCsv: async (): Promise<Blob> => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    const res = await fetch("/api/admin/waitlist-export", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Couldn't export the waitlist (${res.status}).`);
    return res.blob();
  },
  /** Owner-only: every user with their tier, paid status, and marketing consent
   * (for segmentation). Same gate as the overview. */
  usersCsv: async (): Promise<Blob> => {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    const res = await fetch("/api/admin/users-export", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Couldn't export users (${res.status}).`);
    return res.blob();
  },
  /** Owner-only: push all consented users + waitlist leads into the Resend
   * marketing Audience (backfill/reconcile). New opt-ins sync automatically. */
  syncMarketingAudience: () =>
    req<{ ok: boolean; total: number; synced: number; failed: number }>(
      "/api/admin/marketing-sync",
      { method: "POST" },
    ),
  /** List the signed-in user's workspaces (the server ensures at least one exists). */
  workspaces: () => req<{ workspaces: Workspace[] }>("/api/workspaces").then((r) => r.workspaces),
  /** Create a new workspace (brand). */
  createWorkspace: (name: string) =>
    req<{ workspace: Workspace }>("/api/workspaces", {
      method: "POST",
      body: JSON.stringify({ name }),
    }).then((r) => r.workspace),
  /** Rename one of the user's workspaces. */
  renameWorkspace: (id: string, name: string) =>
    req<{ workspace: Workspace }>(`/api/workspaces/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }).then((r) => r.workspace),
  /** Delete a workspace (server refuses the user's last one). */
  deleteWorkspace: (id: string) =>
    req<{ ok: true }>(`/api/workspaces/${id}`, { method: "DELETE" }),
  disconnect: (platform: PlatformId) =>
    req<{ ok: true }>(`/api/accounts/${platform}`, { method: "DELETE" }),
  /** Enqueue a publish. Because some platforms (Instagram) take far longer than
   * a synchronous function allows, the server runs the publish in a background
   * worker and returns `{ queued: true }` immediately — poll History for the
   * outcome. A retry that lands after the first publish already recorded returns
   * `{ queued: false, results }` (the money guard). `idempotencyKey` makes a
   * retry/double-submit publish at most once; reuse it across retries of one
   * publish and rotate it after a successful send. */
  dispatch: (platforms: PlatformId[], draft: PostDraft, idempotencyKey?: string) =>
    req<{ queued: boolean; results?: DispatchResult[] }>("/api/dispatch", {
      method: "POST",
      body: JSON.stringify({ platforms, draft, ...(idempotencyKey ? { idempotencyKey } : {}) }),
    }),
  /** Poll for the recorded outcome of a backgrounded dispatch. Returns
   * `{ record: null }` until the background publisher writes the history row,
   * then the full per-platform result set at once. */
  dispatchStatus: (key: string) =>
    req<{ record: DispatchRecord | null }>(`/api/dispatch/status?key=${encodeURIComponent(key)}`),
  schedule: (platforms: PlatformId[], draft: PostDraft, at: number, idempotencyKey?: string) =>
    req<{ scheduled: ScheduledPost }>("/api/schedule", {
      method: "POST",
      body: JSON.stringify({ platforms, draft, at, ...(idempotencyKey ? { idempotencyKey } : {}) }),
    }),
  unschedule: (id: string) => req<{ ok: true }>(`/api/schedule/${id}`, { method: "DELETE" }),
  /** Add or remove one platform across EVERY scheduled post in the workspace.
   * Adding TikTok requires its Direct-Post options (privacy level + toggles). */
  bulkSetPlatform: (
    platform: PlatformId,
    action: "add" | "remove",
    tiktok?: TikTokOptions,
  ) =>
    req<{ ok: true; updated: number; total: number }>("/api/schedule-bulk-platform", {
      method: "POST",
      body: JSON.stringify({ platform, action, ...(tiktok ? { tiktok } : {}) }),
    }),
  /** Ask the server for a presigned URL to upload a file directly to R2. */
  presignUpload: (input: { filename: string; contentType: string; size: number }) =>
    req<PresignResponse>("/api/upload/presign", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /** Ask the server to tailor the caption/title/tags for each platform. */
  aiCaptions: (draft: { title: string; body: string; tags: string[] }, platforms: PlatformId[]) =>
    req<AiCaptionsResponse>("/api/ai/captions", {
      method: "POST",
      body: JSON.stringify({ draft, platforms }),
    }),
  /** Query the connected TikTok account's Direct-Post options. */
  tiktokCreatorInfo: () =>
    req<TikTokCreatorInfo>("/api/tiktok/creator-info", { method: "POST" }),
  /** List the connected Facebook account's Pages and which one is selected.
   * (Meta App Review: the operator must see their Pages and pick one.) */
  facebookPages: () => req<FacebookPagesResponse>("/api/facebook/pages"),
  /** Record which Facebook Page to publish to. Changeable any time — no reconnect. */
  selectFacebookPage: (pageId: string) =>
    req<{ selectedPageId: string; displayName: string }>("/api/facebook/pages", {
      method: "POST",
      body: JSON.stringify({ pageId }),
    }),
  /** Begin connecting a platform; returns the provider consent URL to navigate to. */
  startConnect: (platform: PlatformId) =>
    req<{ url: string }>(`/api/oauth/start/${platform}`, { method: "POST" }),
  /** Permanently delete the signed-in account and all its data. */
  deleteAccount: () => req<{ ok: true }>("/api/account", { method: "DELETE" }),
  /** Current plan, credit balance, and billing status for the signed-in user. */
  billing: () => req<BillingState>("/api/billing"),
  /** Start a subscription checkout; returns the Stripe-hosted URL to redirect to. */
  checkout: (plan: BillingPlan) =>
    req<{ url: string }>("/api/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ plan }),
    }),
  /** Start a one-time credit top-up checkout; returns the URL to redirect to. */
  topup: () => req<{ url: string }>("/api/billing/topup", { method: "POST" }),
  /** Open the Stripe billing portal; returns the URL to redirect to. */
  billingPortal: () => req<{ url: string }>("/api/billing/portal", { method: "POST" }),
  /** Enable/disable off-session auto top-up when credits run out. */
  setAutoTopup: (enabled: boolean) =>
    req<{ autoTopup: boolean }>("/api/billing/auto-topup", {
      method: "POST",
      body: JSON.stringify({ enabled }),
    }),
  /** The active workspace's saved drafts (most-recently-updated first). */
  drafts: () => req<{ drafts: Draft[] }>("/api/drafts").then((r) => r.drafts),
  /** Create or update a draft; pass `id` to update an already-saved one. */
  saveDraft: (input: {
    id?: string;
    title: string | null;
    platforms: PlatformId[];
    draft: PostDraft;
  }) =>
    req<{ draft: Draft }>("/api/drafts", {
      method: "POST",
      body: JSON.stringify(input),
    }).then((r) => r.draft),
  /** Delete a saved draft. */
  deleteDraft: (id: string) => req<{ ok: true }>(`/api/drafts/${id}`, { method: "DELETE" }),
  /** The active workspace's saved snippets (captions & hashtag groups). */
  snippets: () => req<{ snippets: Snippet[] }>("/api/snippets").then((r) => r.snippets),
  /** Create or update a snippet; pass `id` to update an existing one. */
  saveSnippet: (input: { id?: string; kind: SnippetKind; name: string; content: string }) =>
    req<{ snippet: Snippet }>("/api/snippets", {
      method: "POST",
      body: JSON.stringify(input),
    }).then((r) => r.snippet),
  /** Delete a saved snippet. */
  deleteSnippet: (id: string) => req<{ ok: true }>(`/api/snippets/${id}`, { method: "DELETE" }),
  /** The user's notifications (newest first) plus the unread count for the badge. */
  notifications: () => req<NotificationsResponse>("/api/notifications"),
  /** Mark one notification read, or all of them when passed the literal "all". */
  markNotificationRead: (idOrAll: string) =>
    req<{ ok: true }>("/api/notifications/read", {
      method: "POST",
      body: JSON.stringify(idOrAll === "all" ? { all: true } : { id: idOrAll }),
    }),
  /** Dismiss (delete) one notification. */
  dismissNotification: (id: string) =>
    req<{ ok: true }>(`/api/notifications/${id}`, { method: "DELETE" }),
  /** The signed-in user's notification preferences + marketing-email consent +
   * chosen IANA timezone (notifPrefs empty when none saved; marketingOptIn
   * defaults false; timezone null until the user picks one → browser default). */
  preferences: () =>
    req<{ notifPrefs: NotifPrefs; marketingOptIn: boolean; timezone: string | null }>(
      "/api/preferences",
    ),
  /** Save the user's notification preferences; returns what was stored. */
  savePreferences: (notifPrefs: NotifPrefs) =>
    req<{ notifPrefs: NotifPrefs }>("/api/preferences", {
      method: "POST",
      body: JSON.stringify({ notifPrefs }),
    }).then((r) => r.notifPrefs),
  /** Record the user's marketing-email consent (an affirmative opt-in or a
   * withdrawal). Separate from savePreferences — consent is its own action. */
  setMarketingConsent: (marketingOptIn: boolean) =>
    req<{ marketingOptIn: boolean }>("/api/preferences", {
      method: "POST",
      body: JSON.stringify({ marketingOptIn }),
    }).then((r) => r.marketingOptIn),
  /** Set the user's IANA timezone (or null to clear back to the browser zone);
   * returns what was stored. Its own action, like marketing consent. */
  setTimezone: (timezone: string | null) =>
    req<{ timezone: string | null }>("/api/preferences", {
      method: "POST",
      body: JSON.stringify({ timezone }),
    }).then((r) => r.timezone),
  /** Ask the grounded in-app help assistant a freeform question. `configured`
   * is false when AI help is turned off (the panel then shows the FAQ instead). */
  help: (question: string, history: HelpChatTurn[] = []) =>
    req<{ configured: boolean; answer?: string }>("/api/help", {
      method: "POST",
      body: JSON.stringify({ question, history }),
    }),
};

/** One turn of a help conversation, for the assistant's follow-up context. */
export interface HelpChatTurn {
  role: "user" | "assistant";
  content: string;
}
